import { Button, Fab, MenuProps, Paper, PaperProps, Select, Slide } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { SkodaScrollbars } from '../../scrollbars';
import { ResponsiveSelectProps } from '../responsiveSelect';

const useStyles = makeStyles({ name: 'MobileSelect' })(theme => ({
	paper: {
		padding: '25px 0px 25px 0px',
		top: 'unset !important',
		bottom: '0 !important',
		left: '0 !important',
		right: '0 !important',
		maxWidth: 'unset',
		display: 'flex',
		flexDirection: 'column',
		maxHeight: '75%',
		height: 'auto',
		boxSizing: 'border-box',
		'&.dark': {
			background: theme.palette.tertiary.main
		}
	},
	menuHeader: {
		padding: '6px 16px'
	},
	scrollbars: {
		'&.os-host': {
			overflow: 'hidden !important',
			flex: 1,
			maxHeight: 'unset',
			height: 'unset',
			display: 'flex',
			'& .os-scrollbar': {
				maxHeight: 'inherit',
				'&.os-scrollbar-vertical': {
					width: '17px',
					padding: '0px 10px 0px 0px'
				}
			}
		}
	},
	buttons: {
		flexGrow: 0,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		marginTop: '15px'
	},
	button: {
		marginBottom: '15px',
		'&:last-child': { marginBottom: '0px' },
		'&.cancel': {
			fontSize: '10px',
			fontWeight: 'bold',
			fontStretch: 'normal',
			fontStyle: 'normal',
			lineHeight: 'normal',
			letterSpacing: '1px',
			'&.light': {
				color: theme.palette.text.primary
			},
			'&.dark': {
				color: theme.palette.skodaColors['neutrals-white']
			}
		}
	}
}));

export const MobileSelect = React.forwardRef<HTMLDivElement, ResponsiveSelectProps>((props, ref) => {
	const { textCancel, textConfirm, dark, MenuProps, menuHeader, ...other } = props;
	const [open, setOpen] = useState(false);
	const [value, setValue] = useState(props.value);
	const [children, setChildren] = useState(props.children);
	const confirmMode = textConfirm && textConfirm.length;

	let { cx } = useStyles();

	// sync local value and children with props
	useEffect(() => {
		setValue(props.value);
		setChildren(props.children);
	}, [props.value, props.children]);

	const handleCancel = () => {
		setOpen(false);
		setValue(props.value);
	};

	const handleConfirm = () => {
		setOpen(false);
		const evt = { target: { name: props.name, value } };
		props.onChange(evt as any, undefined);
	};

	const menuProps: Partial<MenuProps> = {
		...MenuProps,
		BackdropProps: {
			onClick: handleCancel
		},
		PaperProps: {
			component: MobileSelectMenu,
			textCancel,
			textConfirm,
			square: true,
			enableConfirm: props.value !== value,
			onCancel: handleCancel,
			onConfirm: handleConfirm,
			dark,
			menuHeader
		} as MobileSelectMenuProps,
		MenuListProps: {
			disablePadding: true
		},
		TransitionComponent: Slide,
		TransitionProps: {
			direction: 'up'
		} as any
	};

	// add dark color class to mobile menu items
	const mobileMenuItems = React.Children.map(children, child => {
		if (React.isValidElement(child)) {
			return React.cloneElement(child, { ...child.props, className: cx(child.props.className, dark && 'dark') });
		}
	});

	return (
		<Select
			ref={ref}
			MenuProps={menuProps}
			{...other}
			value={confirmMode ? value : props.value}
			open={open}
			onOpen={() => setOpen(true)}
			onChange={confirmMode
				? (e) => setValue(e.target.value)
				: (e, c) => { props.onChange(e, c); if (!props.multiple) setOpen(false); }
			}>
			{mobileMenuItems}
		</Select>
	);
});

type MobileSelectMenuProps = {
	textCancel: string;
	textConfirm: string;
	enableConfirm: boolean;
	onCancel: () => void;
	onConfirm: () => void;
	dark?: boolean;
	confirmButton?: React.ReactNode;
	menuHeader: React.ReactNode;
} & PaperProps;

export const MobileSelectMenu = React.forwardRef<HTMLDivElement, MobileSelectMenuProps>((props, ref) => {
	const { children, onCancel, onConfirm, enableConfirm, textCancel, textConfirm, dark, className, confirmButton, menuHeader, ...rest } = props;
	const showConfirm = (textConfirm && textConfirm.length && props.onConfirm) || confirmButton;
	const showCancel = textCancel && textCancel.length > 0;
	const showButtons = showConfirm || showCancel;

	let { classes: styles, cx } = useStyles();

	const _confirmButton = confirmButton ? confirmButton : <ConfirmButton text={textConfirm} enable={enableConfirm} onClick={onConfirm} />;

	return (
		<Paper
			{...rest}
			square
			className={cx(className, styles.paper, dark && 'dark')}
			ref={ref}>
			{menuHeader && <div className={styles.menuHeader}>{menuHeader}</div>}
			<SkodaScrollbars className={styles.scrollbars} light={dark} children={children} />
			{showButtons &&
				<div className={styles.buttons}>
					{showConfirm &&
						_confirmButton}
					{showCancel &&
						<Button
							data-test='button-cancel'
							className={cx(styles.button, 'cancel', dark ? 'dark' : 'light')}
							onClick={onCancel}>
							{textCancel}
						</Button>}
				</div>}
		</Paper>
	);
});

type ConfirmButtonProps = {
	text: string;
	enable: boolean;
	onClick: () => void;
};

export const ConfirmButton = (props: ConfirmButtonProps) => {
	const { text, enable, onClick } = props;
	const { classes: styles, cx } = useStyles();

	return (
		<Fab
			data-test='button-confirm'
			disabled={!enable}
			className={cx(styles.button, 'confirm')}
			variant='extended' color='primary'
			onClick={onClick}>
			{text}
		</Fab>
	);
};

MobileSelect.defaultProps = {
	dark: true
};
